import { TooltipVariant } from "@/constants/tooltip";
import { getApplicationRoot } from "@/utils";
import {
  autoUpdate,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs,
} from "@floating-ui/react";
import clsx from "clsx";
import { cloneElement, forwardRef, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getArrowPlacementSide } from "../popup-menu/utils";
import styles from "./styles.scss";
import { TooltipProps } from "./types";
import { getFloatingMiddlewares } from "./utils";

export const MobileTooltip = forwardRef<HTMLDivElement, TooltipProps>(
  (
    {
      children,
      title,
      className,
      arrowClassName,
      // floatingOptions,
      externalMiddleware = [],
      hideArrow,
      portalRef,
      variant = TooltipVariant.Primary,
    },
    forwardedRef,
  ) => {
    const childrenProps = children.props;
    const isSecondary = variant === TooltipVariant.Secondary;

    const [isTooltipOpened, setIsTooltipOpened] = useState(false);

    const arrowRef = useRef(null);
    const tooltipLocalRef = useRef<HTMLDivElement>(null);

    const {
      x,
      y,
      refs: { setReference, setFloating },
      placement,
      strategy,
      middlewareData: { arrow: { x: arrowX, y: arrowY } = {} },
      context,
    } = useFloating({
      open: isTooltipOpened,
      onOpenChange: setIsTooltipOpened,
      placement: "top",
      middleware: [
        ...getFloatingMiddlewares({ arrowElement: arrowRef }),
        ...externalMiddleware,
      ],
      whileElementsMounted: autoUpdate,
      // ...floatingOptions,
    });

    const click = useClick(context);
    const dismiss = useDismiss(context, { ancestorScroll: true });

    const { getReferenceProps, getFloatingProps } = useInteractions([
      click,
      dismiss,
    ]);

    const arrowPlacementSide = getArrowPlacementSide(placement);
    const componentRef = useMergeRefs([forwardedRef, setReference]);
    const tooltipRef = useMergeRefs([setFloating, tooltipLocalRef]);
    const portalRoot = portalRef?.current || getApplicationRoot();

    const newProps = {
      ...childrenProps,
      ...getReferenceProps(),
      ref: componentRef,
    };

    const renderTooltip = () => (
      <div
        {...getFloatingProps()}
        ref={tooltipRef}
        style={{
          position: strategy,
          top: y ?? "",
          left: x ?? "",
        }}
        className={clsx(
          styles.tooltip,
          isSecondary && styles.backgroundSecondary,
          className,
        )}
      >
        {title}

        {!hideArrow && (
          <div
            ref={arrowRef}
            style={{
              top: arrowY ?? "",
              left: arrowX ?? "",
              [arrowPlacementSide]: -4,
            }}
            className={clsx(
              styles.tooltipArrow,
              isSecondary && styles.backgroundSecondary,
              arrowClassName,
            )}
          />
        )}
      </div>
    );

    return (
      <>
        {cloneElement(children, newProps)}
        {isTooltipOpened && createPortal(renderTooltip(), portalRoot)}
      </>
    );
  },
);
